import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ContactFieldsFragment } from '@/graphql/types';
import { useMemo } from 'react';
import { useAutoFocus } from '../../../../../hooks/useAutoFocus';
import { useContactMutations } from '../../graphql/useContactMutations';
import {
    addNotificationHack,
    NOTIFICATION_VARIANT,
    useNotifications,
} from '../../../../notifications/Notifications';
import type { ContactFormData } from './contactForm.schema';
import { getContactSchema } from './contactForm.schema';
import type { MutationExecutionResult } from '../../graphql/useContactMutationExecutor';

interface UseContactFormProps {
    existingContact: ContactFieldsFragment | undefined;
    draftContact?: Partial<ContactFormData>;
    onClose: () => void;
    onCreateSuccess?: (contact: ContactFieldsFragment) => void;
}

export function useContactForm({
    existingContact,
    onClose,
    draftContact,
    onCreateSuccess,
}: UseContactFormProps) {
    const { create, update, isMutating } = useContactMutations();
    const { addNotification, clearNotifications } = useNotifications();

    const baseData = existingContact ?? draftContact;
    const schema = useMemo(() => getContactSchema(), []);
    const {
        register,
        handleSubmit,
        setError,
        setFocus,
        formState: { errors, isDirty, isValid },
        reset,
    } = useForm<ContactFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: baseData?.name ?? '',
            email: baseData?.email ?? '',
            phoneNumber: baseData?.phoneNumber ?? '',
        },
        mode: 'all',
    });

    // Auto-focus the `name` field on mount.
    useAutoFocus(setFocus, 'name');

    async function onSubmit(data: ContactFormData) {
        clearNotifications();

        let result: MutationExecutionResult;
        const isEdit = !!existingContact?.id;

        const input = {
            name: data.name,
            email: data.email ?? '',
            phoneNumber: data.phoneNumber ?? '',
        };

        if (isEdit) {
            result = await update({ id: existingContact.id, ...input });
        } else {
            result = await create(input);
        }

        if (result.success) {
            if (isEdit) {
                addNotification({
                    message: interpolate(
                        gettext('CONTACT_UPDATE_SUCCESS'),
                        { name: result.contact.name },
                        true
                    ),
                    variant: NOTIFICATION_VARIANT.SUCCESS,
                });
                reset(result.contact);
            } else {
                onCreateSuccess?.(result.contact);
                // Use the page-level notification so the message shows on the page
                // after the modal closes on create.
                addNotificationHack({
                    message: interpolate(
                        gettext('CONTACT_CREATE_SUCCESS'),
                        { name: result.contact.name },
                        true
                    ),
                    variant: NOTIFICATION_VARIANT.SUCCESS,
                });
                onClose();
            }
        } else {
            if (result.fieldErrors) {
                Object.entries(result.fieldErrors).forEach(([field, message]) => {
                    setError(field as keyof ContactFormData, {
                        type: 'server',
                        message,
                    });
                });
            }
            if (result.mutationError) {
                addNotification({
                    message: result.mutationError,
                    variant: NOTIFICATION_VARIANT.ERROR,
                });
            }
        }
    }

    return {
        register,
        onSubmit: handleSubmit(onSubmit),
        isDirty,
        isValid,
        isMutating,
        errors,
    };
}
